<script setup>
import { computed, h, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { picoSearch } from '@chatwoot/pico-search';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';
import SuggestedScenarios from 'dashboard/components-next/captain/assistant/SuggestedRules.vue';
import ScenariosCard from 'dashboard/components-next/captain/assistant/ScenariosCard.vue';
import BulkSelectBar from 'dashboard/components-next/captain/assistant/BulkSelectBar.vue';
import AddNewScenariosDialog from 'dashboard/components-next/captain/assistant/AddNewScenariosDialog.vue';

const { t } = useI18n();
const route = useRoute();
const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();
const { formatMessage } = useMessageFormatter();
const assistantId = computed(() => Number(route.params.assistantId));

const uiFlags = useMapGetter('captainScenarios/getUIFlags');
const isFetching = computed(() => uiFlags.value.fetchingList);
const scenarios = useMapGetter('captainScenarios/getRecords');

const searchQuery = ref('');

const LINK_INSTRUCTION_CLASS =
  '[&_a[href^="tool://"]]:text-n-iris-11 [&_a:not([href^="tool://"])]:text-n-slate-12 [&_a]:pointer-events-none [&_a]:cursor-default';

const renderInstruction = instruction => () =>
  h('span', {
    class: `text-sm text-n-slate-12 py-4 prose prose-sm min-w-0 break-words ${LINK_INSTRUCTION_CLASS}`,
    innerHTML: instruction,
  });

// Suggested example scenarios for quick add
const scenariosExample = [
  {
    id: 1,
    title: 'Comprador Potencial',
    description:
      'Atender clientes que muestran interés en comprar una licencia',
    instruction:
      'Si alguien está interesado en comprar una licencia, pregúntale lo siguiente:\n\n1. ¿Cuántas licencias desea comprar?\n2. ¿Está migrando desde otra plataforma?\n. Una vez recopilados estos detalles, realiza los siguientes pasos:\n1. Agrega una nota privada con la información recopilada usando [Add Private Note](tool://add_private_note)\n2. Agrega la etiqueta "ventas" al contacto usando [Add Label to Conversation](tool://add_label_to_conversation)\n3. Responde diciendo "uno de nosotros se comunicará pronto" y proporciona un tiempo estimado de respuesta, luego [Handoff to Human](tool://handoff)',
    tools: ['add_private_note', 'add_label_to_conversation', 'handoff'],
  },
  {
    id: 2,
    title: 'Cerrar Venta',
    description:
      'Guiar a los clientes a través de los pasos finales para completar una compra',
    instruction:
      'Cuando un cliente esté listo para completar su compra:\n\n1. Confirma los detalles del producto/servicio y el precio\n2. Pregunta si tiene alguna pregunta o inquietud final\n3. Proporciona opciones de pago y próximos pasos\n4. Una vez confirmado, realiza lo siguiente:\n1. Agrega una nota privada con los detalles de compra y preferencias del cliente usando [Add Private Note](tool://add_private_note)\n2. Agrega la etiqueta "listo-para-comprar" para priorizar la conversación usando [Add Label to Conversation](tool://add_label_to_conversation)\n3. Actualiza la prioridad a alta usando [Update Priority](tool://update_priority)\n4. Agradécele y hazle saber que un representante de ventas finalizará la transacción en breve, luego [Handoff to Human](tool://handoff)',
    tools: [
      'add_private_note',
      'add_label_to_conversation',
      'update_priority',
      'handoff',
    ],
  },
  {
    id: 3,
    title: 'Soporte Técnico',
    description:
      'Manejar problemas técnicos y solicitudes de solución de problemas',
    instruction:
      'Cuando un cliente reporte un problema técnico:\n\n1. Pídele que describa el problema en detalle\n2. Solicita información relevante (mensajes de error, navegador/dispositivo, pasos para reproducir)\n3. Intenta proporcionar pasos básicos de solución de problemas desde las FAQ usando [FAQ Lookup](tool://faq_lookup)\n4. Si el problema se resuelve, usa [Resolve Conversation](tool://resolve_conversation)\n5. Si el problema requiere experiencia técnica:\n1. Documenta todos los detalles técnicos en una nota privada usando [Add Private Note](tool://add_private_note)\n2. Agrega la etiqueta "soporte-tecnico" usando [Add Label to Conversation](tool://add_label_to_conversation)\n3. Establece la prioridad en alta si está afectando su negocio usando [Update Priority](tool://update_priority)\n4. Infórmale que un especialista técnico lo asistirá y [Handoff to Human](tool://handoff)',
    tools: [
      'faq_lookup',
      'resolve_conversation',
      'add_private_note',
      'add_label_to_conversation',
      'update_priority',
      'handoff',
    ],
  },
  {
    id: 4,
    title: 'Solicitud de Reembolso',
    description:
      'Procesar solicitudes de reembolso y cancelación profesionalmente',
    instruction:
      'Cuando un cliente solicite un reembolso:\n\n1. Expresa comprensión y pregunta el motivo del reembolso\n2. Verifica los detalles de su compra (número de pedido, fecha, monto)\n3. Verifica si hay algo que pueda resolver su inquietud en su lugar\n4. Si aún desea continuar:\n1. Documenta el motivo del reembolso y los detalles usando [Add Private Note](tool://add_private_note)\n2. Agrega la etiqueta "solicitud-reembolso" usando [Add Label to Conversation](tool://add_label_to_conversation)\n3. Establece la prioridad en alta para asegurar un procesamiento rápido usando [Update Priority](tool://update_priority)\n4. Infórmale sobre el tiempo de procesamiento del reembolso y que un miembro del equipo lo procesará, luego [Handoff to Human](tool://handoff)',
    tools: [
      'add_private_note',
      'add_label_to_conversation',
      'update_priority',
      'handoff',
    ],
  },
  {
    id: 5,
    title: 'Escalar Queja',
    description: 'Manejar clientes molestos y escalar quejas serias',
    instruction:
      'Cuando trates con un cliente molesto o una queja seria:\n\n1. Reconoce su frustración y discúlpate por el inconveniente\n2. Escucha atentamente y déjalo explicar la situación completa\n3. Documenta todo lo que comparta\n4. NO hagas promesas que no puedas cumplir\n5. Toma acción inmediata:\n1. Crea una nota privada detallada con la queja y el sentimiento del cliente usando [Add Private Note](tool://add_private_note)\n2. Agrega la etiqueta "escalacion" usando [Add Label to Conversation](tool://add_label_to_conversation)\n3. Establece la prioridad en urgente usando [Update Priority](tool://update_priority)\n4. Asegúrale que un miembro senior del equipo manejará personalmente su caso y [Handoff to Human](tool://handoff) inmediatamente',
    tools: [
      'add_private_note',
      'add_label_to_conversation',
      'update_priority',
      'handoff',
    ],
  },
];

const filteredScenarios = computed(() => {
  const query = searchQuery.value.trim();
  const source = scenarios.value;
  if (!query) return source;
  return picoSearch(source, query, ['title', 'description', 'instruction']);
});

const shouldShowSuggestedRules = computed(() => {
  return uiSettings.value?.show_scenarios_suggestions !== false;
});

const closeSuggestedRules = () => {
  updateUISettings({ show_scenarios_suggestions: false });
};

// Bulk selection & hover state
const bulkSelectedIds = ref(new Set());
const hoveredCard = ref(null);

const handleRuleSelect = id => {
  const selected = new Set(bulkSelectedIds.value);
  selected[selected.has(id) ? 'delete' : 'add'](id);
  bulkSelectedIds.value = selected;
};

const buildSelectedCountLabel = computed(() => {
  const count = scenarios.value.length || 0;
  const isAllSelected = bulkSelectedIds.value.size === count && count > 0;
  return isAllSelected
    ? t('CAPTAIN.ASSISTANTS.SCENARIOS.BULK_ACTION.UNSELECT_ALL', { count })
    : t('CAPTAIN.ASSISTANTS.SCENARIOS.BULK_ACTION.SELECT_ALL', { count });
});

const selectedCountLabel = computed(() => {
  return t('CAPTAIN.ASSISTANTS.SCENARIOS.BULK_ACTION.SELECTED', {
    count: bulkSelectedIds.value.size,
  });
});

const handleRuleHover = (isHovered, id) => {
  hoveredCard.value = isHovered ? id : null;
};

const getToolsFromInstruction = instruction => [
  ...new Set(
    [...(instruction?.matchAll(/\(tool:\/\/([^)]+)\)/g) ?? [])].map(m => m[1])
  ),
];

const updateScenario = async scenario => {
  try {
    await store.dispatch('captainScenarios/update', {
      id: scenario.id,
      assistantId: assistantId.value,
      ...scenario,
      tools: getToolsFromInstruction(scenario.instruction),
    });
    useAlert(t('CAPTAIN.ASSISTANTS.SCENARIOS.API.UPDATE.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('CAPTAIN.ASSISTANTS.SCENARIOS.API.UPDATE.ERROR');
    useAlert(errorMessage);
  }
};

const deleteScenario = async id => {
  try {
    await store.dispatch('captainScenarios/delete', {
      id,
      assistantId: assistantId.value,
    });
    useAlert(t('CAPTAIN.ASSISTANTS.SCENARIOS.API.DELETE.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('CAPTAIN.ASSISTANTS.SCENARIOS.API.DELETE.ERROR');
    useAlert(errorMessage);
  }
};

// TODO: Add bulk delete endpoint
const bulkDeleteScenarios = async ids => {
  const idsArray = ids || Array.from(bulkSelectedIds.value);
  await Promise.all(
    idsArray.map(id =>
      store.dispatch('captainScenarios/delete', {
        id,
        assistantId: assistantId.value,
      })
    )
  );
  bulkSelectedIds.value = new Set();
  useAlert(t('CAPTAIN.ASSISTANTS.SCENARIOS.API.DELETE.SUCCESS'));
};

const addScenario = async scenario => {
  try {
    await store.dispatch('captainScenarios/create', {
      assistantId: assistantId.value,
      ...scenario,
      tools: getToolsFromInstruction(scenario.instruction),
    });
    useAlert(t('CAPTAIN.ASSISTANTS.SCENARIOS.API.ADD.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('CAPTAIN.ASSISTANTS.SCENARIOS.API.ADD.ERROR');
    useAlert(errorMessage);
  }
};

const addAllExampleScenarios = async () => {
  try {
    scenariosExample.forEach(async scenario => {
      await store.dispatch('captainScenarios/create', {
        assistantId: assistantId.value,
        ...scenario,
      });
    });
    useAlert(t('CAPTAIN.ASSISTANTS.SCENARIOS.API.ADD.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('CAPTAIN.ASSISTANTS.SCENARIOS.API.ADD.ERROR');
    useAlert(errorMessage);
  }
};

onMounted(() => {
  store.dispatch('captainScenarios/get', {
    assistantId: assistantId.value,
  });
  store.dispatch('captainTools/getTools');
});
</script>

<template>
  <PageLayout
    :header-title="$t('CAPTAIN.ASSISTANTS.SCENARIOS.TITLE')"
    :is-fetching="isFetching"
    :show-know-more="false"
    :show-pagination-footer="false"
  >
    <template #body>
      <SettingsHeader
        :heading="$t('CAPTAIN.ASSISTANTS.SCENARIOS.TITLE')"
        :description="$t('CAPTAIN.ASSISTANTS.SCENARIOS.DESCRIPTION')"
      />
      <div v-if="shouldShowSuggestedRules" class="flex mt-7 flex-col gap-4">
        <SuggestedScenarios
          :title="$t('CAPTAIN.ASSISTANTS.SCENARIOS.ADD.SUGGESTED.TITLE')"
          :items="scenariosExample"
          @close="closeSuggestedRules"
          @add="addAllExampleScenarios"
        >
          <template #default="{ item }">
            <div class="flex items-center gap-3 justify-between">
              <span class="text-sm text-n-slate-12">
                {{ item.title }}
              </span>
              <Button
                :label="
                  $t('CAPTAIN.ASSISTANTS.SCENARIOS.ADD.SUGGESTED.ADD_SINGLE')
                "
                ghost
                xs
                slate
                class="!text-sm !text-n-slate-11 flex-shrink-0"
                @click="addScenario(item)"
              />
            </div>
            <div class="flex flex-col">
              <span class="text-sm text-n-slate-11 mt-2">
                {{ item.description }}
              </span>
              <component
                :is="renderInstruction(formatMessage(item.instruction, false))"
              />
              <span class="text-sm text-n-slate-11 font-medium mb-1">
                {{ t('CAPTAIN.ASSISTANTS.SCENARIOS.ADD.SUGGESTED.TOOLS_USED') }}
                {{ item.tools?.map(tool => `@${tool}`).join(', ') }}
              </span>
            </div>
          </template>
        </SuggestedScenarios>
      </div>
      <div class="flex mt-7 flex-col gap-4">
        <div class="flex justify-between items-center">
          <BulkSelectBar
            v-model="bulkSelectedIds"
            :all-items="scenarios"
            :select-all-label="buildSelectedCountLabel"
            :selected-count-label="selectedCountLabel"
            :delete-label="
              $t('CAPTAIN.ASSISTANTS.SCENARIOS.BULK_ACTION.BULK_DELETE_BUTTON')
            "
            @bulk-delete="bulkDeleteScenarios"
          >
            <template #default-actions>
              <AddNewScenariosDialog @add="addScenario" />
            </template>
          </BulkSelectBar>
          <div
            v-if="scenarios.length && bulkSelectedIds.size === 0"
            class="max-w-[22.5rem] w-full min-w-0"
          >
            <Input
              v-model="searchQuery"
              :placeholder="
                t('CAPTAIN.ASSISTANTS.SCENARIOS.LIST.SEARCH_PLACEHOLDER')
              "
            />
          </div>
        </div>
        <div v-if="scenarios.length === 0" class="mt-1 mb-2">
          <span class="text-n-slate-11 text-sm">
            {{ t('CAPTAIN.ASSISTANTS.SCENARIOS.EMPTY_MESSAGE') }}
          </span>
        </div>
        <div v-else-if="filteredScenarios.length === 0" class="mt-1 mb-2">
          <span class="text-n-slate-11 text-sm">
            {{ t('CAPTAIN.ASSISTANTS.SCENARIOS.SEARCH_EMPTY_MESSAGE') }}
          </span>
        </div>
        <div v-else class="flex flex-col gap-2">
          <ScenariosCard
            v-for="scenario in filteredScenarios"
            :id="scenario.id"
            :key="scenario.id"
            :title="scenario.title"
            :description="scenario.description"
            :instruction="scenario.instruction"
            :tools="scenario.tools"
            :is-selected="bulkSelectedIds.has(scenario.id)"
            :selectable="
              hoveredCard === scenario.id || bulkSelectedIds.size > 0
            "
            @select="handleRuleSelect"
            @delete="deleteScenario(scenario.id)"
            @update="updateScenario"
            @hover="isHovered => handleRuleHover(isHovered, scenario.id)"
          />
        </div>
      </div>
    </template>
  </PageLayout>
</template>
